import { PenIcon, TrashIcon } from "@xola/icons";
import { Badge } from "@xola/ui-kit";
import clsx from "clsx";
import React from "react";
import { MessageTemplate } from "types";
import { InlineConfirmDeletePopover } from "../../Common/InlineConfirmDeletePopover";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";

interface TemplateItemProps {
    template: MessageTemplate;
    isCollapsed?: boolean;
    isActive?: boolean;
    className?: string;
    onClick: (template: MessageTemplate) => void;
    onDelete: (template: MessageTemplate) => void;
}

export const TemplateItem = ({
    template,
    isCollapsed = false,
    isActive = false,
    onClick,
    className,
    onDelete,
}: TemplateItemProps) => {
    const handleClick = () => onClick(template);

    return (
        <div
            className={clsx(
                "flex h-20 w-full items-center overflow-hidden border-b border-b-gray-light px-6 py-4.5 hover:bg-gray-lighter",
                isCollapsed && "cursor-pointer",
                isActive && "bg-blue-lighter",
                className,
            )}
            data-testid="template-item"
            onClick={() => {
                if (isCollapsed) handleClick();
            }}
        >
            <div className="flex-shrink-0 space-y-1">
                <div
                    className={clsx(
                        "flex space-x-2 truncate text-lg leading-p2 tracking-tightest",
                        isCollapsed && "flex-col space-x-0",
                    )}
                >
                    <NoTranslate className="font-bold">{template.name}</NoTranslate>
                    <BadgeRenderer key={template.id} template={template} />
                </div>
            </div>

            {isCollapsed ? null : (
                <div className="mr-2 flex flex-auto justify-end space-x-4 text-lg font-bold">
                    <div className="space-x-2">
                        <SecondaryButton size="small" data-testid="template-item-edit-button" onClick={handleClick}>
                            <PenIcon />
                        </SecondaryButton>
                        <InlineConfirmDeletePopover
                            placement="left"
                            appendTo={document.body}
                            confirmButtonText="Confirm"
                            onConfirm={() => onDelete(template)}
                        >
                            <SecondaryButton size="small" data-testid="template-item-delete-button">
                                <TrashIcon />
                            </SecondaryButton>
                        </InlineConfirmDeletePopover>
                    </div>
                </div>
            )}
        </div>
    );
};

const BadgeRenderer = ({ template }: { template: MessageTemplate }) => {
    const { email, sms } = template;
    return (
        <div className="flex space-x-2">
            {email?.body && (
                <Badge color="secondary" size="small" data-testid="template-item-email-badge">
                    Email
                </Badge>
            )}
            {sms?.body && (
                <Badge color="secondary" size="small" data-testid="template-item-text-message-badge">
                    Text Message
                </Badge>
            )}
        </div>
    );
};
